import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import CalciteModal from '@/components/shared/CalciteModal';
import CalciteButton from '@/components/shared/CalciteButton';
import {MODAL_REGISTRY} from '@/features/product/product-modal/config/product-modal-registry';
import {mapLineItem} from '@/services/api/utils/cart-line-item-mapper';
import {CART_SOURCE_TYPES} from '@/services/api/api-constants';
import AddToCartButton from '@/features/product/components/AddToCartButton';
import ProductErrorMessage from '@/features/product/components/ProductErrorMessage';
import useFormState from '@/features/product/hooks/useFormState';
import {getSkuByProductPath, isProductPurchasable} from '@/features/product/utils/product-model-utils';
import ContactButton from '@/features/product/components/ContactButton';
import {PRODUCT_TYPES} from '@/features/product/product-constants';
/**
 * Generic modal dialog for standalone products that go directly to checkout.
 * The `type` prop drives a registry lookup that supplies the title, labels,
 * content component, and submit rules — no changes here are needed to support
 * additional product types.
 * @param {Object} props
 * @param {string} props.id - ID applied to the modal element
 * @param {boolean} props.open - Whether the modal is open
 * @param {string} props.type - Product type key (must exist in MODAL_REGISTRY)
 * @param {Object} props.productData - Normalized product pricing data from RTK Query cache
 * @param {Object} props.labels - AEM-authored label strings
 * @param {Object} props.error - Error object if pricing data failed to load
 * @param {string} props.defaultProductPath - The default product path to select when the modal opens (optional)
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSubmit - Submit handler; receives the payload from getSubmitPayload
 * @returns {JSX.Element|null} Modal component or null if type is unrecognised
 */
const ProductModal = ({
  id,
  open,
  type,
  productData,
  labels,
  error,
  defaultProductPath,
  onClose,
  onSubmit,
}) => {
  const config = MODAL_REGISTRY[type];
  const defaultActiveSku = getSkuByProductPath(productData?.productModels, defaultProductPath);

  const [state, setState] = useState(() => {
    const initial = config?.initialState ?? {};
    return defaultActiveSku ? {...initial, selectedProduct: defaultActiveSku} : initial;
  });
  const [formState, {markAsDirty, markAllAsTouched, reset}] = useFormState(config?.validate, state);

  if (!config) return null;

  const title = labels?.title ?? '';
  const cancelLabel = labels?.cancelLabel ?? '';
  const isSignedIn = !!Cookies.get('esri_auth');
  const submitLabel = (isSignedIn ? labels?.submitLabelSignedIn : labels?.submitLabelSignedOut) ?? '';
  const currency = {
    currencyPlacement: productData?.locale?.currencyPlacement,
    currencySymbol: productData?.locale?.currencySymbol,
    currencyCode: productData?.locale?.currency,
  };
  const {ContentComponent, getSubmitPayload} = config;

  const selectedModel = productData?.productModels
    ?.flatMap((m) => (Array.isArray(m) ? m : [m]))
    ?.find((m) => m.activeSku === state.selectedProduct);
  const isTrainingVoucherOutsideUs = type === PRODUCT_TYPES.TRAINING_VOUCHERS
    && !!productData?.locale
    && productData.locale.zone !== 'US';

  const items = selectedModel
    ? [
        mapLineItem(selectedModel, {
          sourceType: CART_SOURCE_TYPES.PRODUCT_PRICING_MODEL,
          quantity: state.quantity ?? 1,
        }),
      ]
    : [];

  const handleClose = () => {
    setState(config.initialState);
    reset();
    if (onClose) onClose();
  };

  const handleSubmit = () => {
    if (onSubmit) onSubmit(getSubmitPayload(state));
    globalThis?.location?.assign(globalThis?.storeVariables?.checkoutUrl ?? '/checkout');
  };

  const renderContent = () => {
    if (error)
      return <ProductErrorMessage title={labels?.unexpectedErrorTitle} message={labels?.unexpectedErrorMessage} />;
    return (
      <ContentComponent
        products={productData?.productModels?.map((model) => ({
          name: model.title,
          ...model.products?.[0],
        }))}
        defaultProduct={defaultActiveSku}
        currency={currency}
        formattedLocale={productData?.locale?.formattedLocale}
        state={state}
        onChange={(next) => {
          const nextState = typeof next === 'function' ? next(state) : next;
          Object.keys(nextState).forEach((key) => {
            if (nextState[key] !== state[key]) markAsDirty(key);
          });
          setState(next);
        }}
        labels={labels}
        formState={formState}
      />
    );
  };

  return (
    <CalciteModal
      id={id}
      className={`product-modal ${type}-modal`}
      open={open}
      onClose={handleClose}
      outsideCloseDisabled={true}
      kind="brand"
      width="s"
      scale="s"
    >
      <h2 className="product-modal__header" slot="header">
        {title}
      </h2>

      <div slot="content" className="product-modal__content">
        {renderContent()}
      </div>

      <CalciteButton slot="back" appearance="outline" kind="brand" onClick={handleClose}>
        {cancelLabel}
      </CalciteButton>
      {!isTrainingVoucherOutsideUs && isProductPurchasable(productData?.productModels, state.selectedProduct)
        ? (
            <AddToCartButton
              slot="primary"
              items={items}
              allowUpdate={false}
              notifyOnAdd={false}
              pricingLocale={productData?.locale}
              onSuccess={handleSubmit}
              onError={handleClose}
              additionalCartMetadata={{
                referrer: `${globalThis.location.origin}${globalThis.location.pathname}`,
              }}
              onBeforeSubmit={() => {
                markAllAsTouched();
                return formState.valid;
              }}
            >
              {submitLabel}
            </AddToCartButton>
          )
        : (
            <ContactButton slot="primary" url={labels?.contactUsLink}>
              {labels?.contactUsLabel}
            </ContactButton>
          )}
    </CalciteModal>
  );
};

ProductModal.propTypes = {
  id: PropTypes.string,
  open: PropTypes.bool,
  type: PropTypes.string.isRequired,
  productData: PropTypes.object,
  labels: PropTypes.shape({
    title: PropTypes.string,
    cancelLabel: PropTypes.string,
    submitLabelSignedOut: PropTypes.string,
    submitLabelSignedIn: PropTypes.string,
    unexpectedErrorTitle: PropTypes.string,
    unexpectedErrorMessage: PropTypes.string,
    contactUsLabel: PropTypes.string,
    contactUsLink: PropTypes.string,
  }),
  error: PropTypes.object,
  defaultProductPath: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default ProductModal;
